import { createFileRoute, Link } from "@tanstack/react-router";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useEffect, useState } from "react";
import {
  ArrowRight, MapPin, Search, Bookmark, Rocket, Star, Compass,
  Trees, Waves, Palette, ShoppingBag, Trophy, CloudRain,
} from "lucide-react";
import { DEFAULT_REGION } from "@/lib/regions";
import { getWeatherForecast, type WeatherForecast } from "@/lib/weather.functions";
import { supabase } from "@/integrations/supabase/client";
import { getWeatherIcon } from "@/lib/weather-display";
import { buildSeoMeta, SITE_URL, SITE_NAME } from "@/lib/seo";

export const Route = createFileRoute("/")({
  head: () => {
    const seo = buildSeoMeta({
      title: "Things To Do In Lakewood Ranch, FL — RanchPlanner",
      description:
        "Discover the best things to do in Lakewood Ranch, FL. Personalized picks for activities, events, parks, restaurants, and weekend plans near you.",
      path: "/",
    });
    const localBusinessJsonLd = {
      "@context": "https://schema.org",
      "@type": "TouristInformationCenter",
      name: SITE_NAME,
      url: SITE_URL,
      description:
        "Local recommendations for things to do in and around Lakewood Ranch, Florida.",
      areaServed: [
        { "@type": "City", name: "Lakewood Ranch", "@id": "https://en.wikipedia.org/wiki/Lakewood_Ranch,_Florida" },
        { "@type": "City", name: "Sarasota" },
        { "@type": "City", name: "Bradenton" },
      ],
      address: {
        "@type": "PostalAddress",
        addressLocality: "Lakewood Ranch",
        addressRegion: "FL",
        postalCode: "34202",
        addressCountry: "US",
      },
      geo: { "@type": "GeoCoordinates", latitude: 27.4015, longitude: -82.3959 },
    };
    return {
      ...seo,
      scripts: [{ type: "application/ld+json", children: JSON.stringify(localBusinessJsonLd) }],
    };
  },
  component: HomePage,
});

/* ─── Decorative rotating blob (unchanged from prior production) ─── */
function FloatingBlob({ color, className }: { color: string; className?: string }) {
  return (
    <motion.svg viewBox="0 0 200 200" className={className} animate={{ rotate: [0, 360] }} transition={{ duration: 30, repeat: Infinity, ease: "linear" }}>
      <path d="M44.7,-76.4C58.8,-69.2,71.8,-58.9,79.6,-45.5C87.3,-32.1,89.9,-16.1,88.1,-0.9C86.4,14.2,80.4,28.5,72.1,41.1C63.8,53.7,53.3,64.7,40.4,71.9C27.5,79.1,12.3,82.4,-2.2,86.2C-16.7,90,-33.4,94.3,-46.5,87.3C-59.6,80.3,-69.1,62,-76.4,44.1C-83.7,26.1,-88.9,8.6,-87.2,-8.1C-85.5,-24.7,-77,-40.5,-65.2,-51.7C-53.5,-62.9,-38.6,-69.4,-24.1,-76C-9.5,-82.7,4.7,-89.6,19.6,-87.5C34.5,-85.5,50.1,-74.6,44.7,-76.4Z" transform="translate(100 100)" fill={color} />
    </motion.svg>
  );
}

function SparkleParticle({ delay, x, y }: { delay: number; x: string; y: string }) {
  return (
    <motion.div className="absolute pointer-events-none" style={{ left: x, top: y }}
      animate={{ opacity: [0, 1, 0], scale: [0.5, 1.2, 0.5] }}
      transition={{ duration: 2.5, delay, repeat: Infinity, ease: "easeInOut" }}
    >
      <Star className="w-3 h-3 text-primary fill-primary" />
    </motion.div>
  );
}

/* ─── Credibility strip — replaces the giant "Lakewood Ranch, FL" stamp.
       Production palette only; no logo symbol per spec. ─── */
function CredibilityStrip() {
  return (
    <section className="px-6 md:px-16 lg:px-24 pt-2 pb-3">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto rounded-2xl px-5 md:px-7 py-5 md:py-6"
        style={{
          background: "color-mix(in srgb, var(--color-primary) 8%, var(--color-card))",
          border: "1px solid color-mix(in srgb, var(--color-primary) 18%, transparent)",
        }}
      >
        <p
          className="text-[15px] md:text-[18px] font-black uppercase tracking-[0.22em] mb-2 text-primary"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          Built for {DEFAULT_REGION.name}, FL
        </p>
        <p
          className="text-[24px] md:text-[34px] leading-[1.05] text-foreground"
          style={{ fontFamily: "'Caveat', cursive" }}
        >
           Neighborly, by design — by people who live and explore here.
        </p>
      </motion.div>
    </section>
  );
}

/* ─── Hero — focused, single-CTA, no second giant place stamp ─── */
function HeroSection() {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const smoothX = useSpring(mouseX, { stiffness: 50, damping: 20 });
  const smoothY = useSpring(mouseY, { stiffness: 50, damping: 20 });

  useEffect(() => {
    const handleMouse = (e: MouseEvent) => {
      mouseX.set((e.clientX - window.innerWidth / 2) / 25);
      mouseY.set((e.clientY - window.innerHeight / 2) / 25);
    };
    window.addEventListener("mousemove", handleMouse);
    return () => window.removeEventListener("mousemove", handleMouse);
  }, [mouseX, mouseY]);

  return (
    <section className="relative pb-10 md:pb-16 pt-4 md:pt-6 overflow-visible bg-background">
      {/* Production rotating blobs */}
      <motion.div className="absolute top-0 -right-20 w-[320px] h-[320px] md:w-[500px] md:h-[500px] md:-top-20 opacity-25"
        style={{ x: useTransform(smoothX, v => -v * 1.5), y: useTransform(smoothY, v => -v * 1.5) }}>
        <FloatingBlob color="oklch(0.65 0.13 220)" className="w-full h-full" />
      </motion.div>
      <motion.div className="absolute top-1/2 -left-32 w-[400px] h-[400px] opacity-20"
        style={{ x: useTransform(smoothX, v => v * 0.8), y: useTransform(smoothY, v => v * 0.8) }}>
        <FloatingBlob color="oklch(0.82 0.28 145)" className="w-full h-full" />
      </motion.div>

      <div className="relative z-10 px-6 md:px-16 lg:px-24 max-w-7xl w-full pt-2 md:pt-4">
        {/* Accessible, SEO-anchored H1 — visually hidden but indexable.
            The decorative "Venture Out. LWR." headline below stays visual. */}
        <h1 className="sr-only">Things To Do In Lakewood Ranch, FL</h1>
        <motion.div
          aria-hidden="true"
          className="text-[3rem] sm:text-6xl md:text-7xl lg:text-8xl font-black leading-[0.9] tracking-[-0.03em] text-foreground mb-6"
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          style={{ fontFamily: "'Syne', sans-serif" }}
        >
          Venture Out<span className="text-highlight">.</span>{" "}
          <span className="relative inline-block">
            <span className="relative z-10 px-3 md:px-5 py-0.5 md:py-1 bg-primary text-white">LWR</span>
          </span>
        </motion.div>

        <motion.p
          className="mt-4 text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.35 }}
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
           A tailored shortlist of the best places to go this weekend in and around Lakewood Ranch — picked by neighbors, ranked for you.
        </motion.p>

        <motion.div
          className="flex flex-nowrap gap-3 items-center"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.55 }}
        >
          <Link
            to="/discover"
            className="group inline-flex items-center gap-2 md:gap-3 px-6 md:px-10 py-4 md:py-5 rounded-full text-lg md:text-2xl font-bold text-white whitespace-nowrap"
            style={{
              background: "var(--color-primary)",
              boxShadow: "0 10px 30px color-mix(in srgb, var(--color-primary) 25%, transparent)",
              fontFamily: "'Syne', sans-serif",
            }}
          >
            Discover <ArrowRight className="w-5 h-5 md:w-6 md:h-6 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/explore"
            className="inline-flex items-center gap-2 px-4 md:px-5 py-3 rounded-full text-sm md:text-base font-semibold text-foreground hover:bg-foreground/5 transition-all border border-foreground/20 whitespace-nowrap"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            <Compass className="w-4 h-4" /> Explore All
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── How it works — compact horizontal strip ─── */
const STEPS = [
  { title: "Discover", icon: Search, desc: "Answer a few quick questions and we'll tailor your picks." },
  { title: "Explore", icon: Bookmark, desc: "Review smart recommendations with key info. Save your favorites." },
  { title: "Plan", icon: Rocket, desc: "Build your itinerary. Save, share, and go have fun." },
];

function HowItWorksStrip() {
  return (
    <section className="px-6 md:px-16 lg:px-24 pt-0 pb-10">
      <div
        className="max-w-7xl mx-auto rounded-2xl px-5 md:px-7 py-5 md:py-6"
        style={{
          background: "color-mix(in srgb, var(--color-primary) 12%, var(--color-card))",
          border: "1px solid color-mix(in srgb, var(--color-primary) 25%, transparent)",
        }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.title} className="flex items-start gap-3">
                <div className="w-9 h-9 shrink-0 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-black"
                  style={{ fontFamily: "'Syne', sans-serif" }}>
                  {i + 1}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <Icon className="w-4 h-4 text-foreground/70" />
                    <h3 className="text-base font-black text-foreground" style={{ fontFamily: "'Syne', sans-serif" }}>
                      {s.title}
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-snug" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {s.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ─── Featured + Weather (consolidated) ─── */
interface HomeEventPreview {
  title: string;
  description: string;
  category: string;
  cost_level: string;
  cost_details: string;
  indoor_outdoor: string;
  drive_minutes_from_lwr: number;
}

const featuredFallback: HomeEventPreview = {
  title: "Robinson Preserve",
  description: "Kayak through mangrove tunnels, spot manatees, and hit observation towers — all free.",
  category: "Parks",
  cost_level: "Free",
  cost_details: "Free",
  indoor_outdoor: "outdoor",
  drive_minutes_from_lwr: 25,
};

function parseHomeCost(text: string): number {
  if (/free/i.test(text)) return 0;
  const m = [...text.matchAll(/\$(\d+)/g)];
  if (!m.length) return 999;
  return Math.min(...m.map((x) => parseInt(x[1], 10)));
}

function FeaturedAndWeather() {
  const [weather, setWeather] = useState<WeatherForecast | null>(null);
  const [featured, setFeatured] = useState<HomeEventPreview>(featuredFallback);
  const [moodCategories, setMoodCategories] = useState<string[]>([
    "Parks", "Markets", "Nature", "Art", "Live Music", "Family Fun",
  ]);
  const [todayStr, setTodayStr] = useState<string>("");
  const [todayLabel, setTodayLabel] = useState<string>("");
  const WeatherIcon = getWeatherIcon(weather?.current?.condition || weather?.days[0]?.condition);

  useEffect(() => {
    const d = new Date();
    const s = d.toISOString().split("T")[0];
    setTodayStr(s);
    setTodayLabel(d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }));
  }, []);

  useEffect(() => {
    if (!todayStr) return;
    const end = new Date(todayStr + "T00:00:00");
    end.setDate(end.getDate() + 3);
    const endStr = end.toISOString().split("T")[0];
    getWeatherForecast({ data: { startDate: todayStr, endDate: endStr } }).then(setWeather).catch(() => {});
  }, [todayStr]);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from("events")
        .select("title, description, category, cost_level, cost_details, indoor_outdoor, drive_minutes_from_lwr")
        .eq("is_active", true).is("parent_id", null);
      if (error || !data?.length) return;
      const events = data as HomeEventPreview[];
      const cats = [...new Set(events.map((e) => e.category).filter(Boolean))].sort();
      if (cats.length) setMoodCategories(cats.slice(0, 8));
      const freeParks = events.filter((e) => {
        const minCost = Math.min(parseHomeCost(e.cost_level), parseHomeCost(e.cost_details));
        return /park/i.test(e.category) && minCost === 0;
      });
      if (freeParks.length) setFeatured(freeParks[Math.floor(Math.random() * freeParks.length)]);
    })();
  }, []);

  const settingLabel =
    featured.indoor_outdoor === "both" ? "Indoor + Outdoor"
      : featured.indoor_outdoor === "indoor" ? "Indoor" : "Outdoor";

  return (
    <section className="pb-12 md:pb-16 px-6 md:px-16 lg:px-24 bg-background">
      <div className="max-w-7xl mx-auto">
        <motion.div
          className="mb-8 md:mb-10 max-w-3xl"
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-6xl font-black text-foreground leading-[0.95] mb-3" style={{ fontFamily: "'Syne', sans-serif" }}>
            Your <span className="text-accent">weekend</span>, sorted<span className="text-highlight">.</span>
          </h2>
          <p className="text-muted-foreground text-base md:text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Hundreds of hand-vetted spots. Hours of local research. Distilled into one quick tool.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
          <motion.div
            className="md:col-span-8 rounded-[2rem] p-7 md:p-9 relative overflow-hidden group"
            style={{ background: "var(--color-card)", border: "1px solid var(--color-border)" }}
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          >
            <div className="flex items-center justify-between gap-3 mb-5">
              <span className="px-3 py-1 rounded-full text-xs font-bold bg-primary text-primary-foreground"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}>FEATURED</span>
            </div>
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-black text-foreground mb-3 leading-tight" style={{ fontFamily: "'Syne', sans-serif" }}>
              {featured.title}
            </h3>
            <p className="text-muted-foreground text-base md:text-lg mb-6 max-w-xl leading-relaxed" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {featured.description}
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-highlight text-highlight-foreground">Free</span>
              <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-accent text-accent-foreground">{settingLabel}</span>
              <span className="px-3 py-1.5 rounded-full text-xs font-bold bg-foreground/10 text-foreground border border-foreground/20">
                {featured.drive_minutes_from_lwr} min
              </span>
            </div>
          </motion.div>

          <motion.div
            className="md:col-span-4 rounded-[2rem] p-5 md:p-6 flex flex-col justify-between relative overflow-hidden text-white min-w-0"
            style={{ background: "linear-gradient(160deg, var(--color-accent), oklch(0.5 0.15 220))" }}
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }}
          >
            {(() => {
              const today = weather?.days[0];
              const condition = weather?.current?.condition || today?.condition || "—";
              const temp = weather?.current?.temp ?? today?.tempHigh;
              const precip = today?.precipChance ?? weather?.precipChance ?? 0;
              const recommendation = weather?.isRainy || precip >= 50
                ? "Good day for indoor picks"
                : (today?.tempHigh ?? 0) >= 88
                  ? "Hot — go early or hit the water"
                  : "Great day to get outside";
              const upcoming = (weather?.days ?? []).slice(1, 4);
              return (
                <>
                  <div className="min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-4">
                      <div className="w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
                        <WeatherIcon className="w-5 h-5" />
                      </div>
                      <p className="text-[11px] text-white/90 uppercase tracking-[0.15em] text-right" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                        {todayLabel}
                      </p>
                    </div>
                    <p className="text-5xl font-black leading-none mb-1" style={{ fontFamily: "'Syne', sans-serif" }}>
                      {temp != null ? `${temp}°` : "—"}
                    </p>
                    <p className="text-sm font-semibold text-white capitalize mb-2 truncate" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      {condition.toLowerCase()}
                    </p>
                    {today && (
                      <p className="text-xs text-white/90 mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                        H {today.tempHigh}° · L {today.tempLow}° · {precip}%
                      </p>
                    )}
                    <p className="text-sm font-bold leading-snug" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      {recommendation}
                    </p>
                  </div>
                  {upcoming.length > 0 && (
                    <div className="mt-5 pt-4 border-t border-white/20 grid grid-cols-3 gap-2">
                      {upcoming.map((d) => {
                        const DIcon = getWeatherIcon(d.condition);
                        const dt = new Date(d.date + "T00:00:00");
                        const label = dt.toLocaleDateString("en-US", { weekday: "short" });
                        return (
                          <div key={d.date} className="flex flex-col items-center gap-1">
                            <span className="text-[10px] uppercase tracking-wider text-white/90" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{label}</span>
                            <DIcon className="w-4 h-4" />
                            <span className="text-xs font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{d.tempHigh}°</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              );
            })()}
          </motion.div>

          <motion.div
            className="md:col-span-12 rounded-[2rem] p-7 relative overflow-hidden"
            style={{ background: "var(--color-card)", border: "1px solid var(--color-border)" }}
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }}
          >
            <p className="text-muted-foreground text-xs uppercase tracking-[0.2em] mb-3 font-medium" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Something for every mood
            </p>
            <div className="flex flex-wrap gap-2">
              {moodCategories.map((label, i) => {
                const color = ["var(--color-highlight)", "var(--color-primary)", "var(--color-accent)"][i % 3];
                return (
                  <span key={label} className="px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-default hover:scale-105"
                    style={{
                      background: `color-mix(in srgb, ${color} 15%, transparent)`,
                      color,
                      border: `1px solid color-mix(in srgb, ${color} 30%, transparent)`,
                      fontFamily: "'Space Grotesk', sans-serif",
                    }}>
                    {label}
                  </span>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ─── Final CTA — slim ─── */
function FinalCTA() {
  return (
    <section className="relative pt-6 md:pt-8 pb-16 md:pb-20 overflow-hidden bg-background">
      <div className="relative z-10 px-6 md:px-16 lg:px-24 max-w-7xl mx-auto text-center">
        <motion.h2
          className="text-4xl md:text-6xl font-black text-foreground mb-4 leading-[0.95]"
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          style={{ fontFamily: "'Syne', sans-serif" }}
        >
          Ready to <span className="text-highlight">go</span>?
        </motion.h2>
        <p
          className="text-2xl md:text-3xl mb-7 max-w-md mx-auto text-foreground"
          style={{ fontFamily: "'Caveat', cursive" }}
        >
          Tell us your style — we'll do the legwork.
        </p>
        <Link to="/discover"
          className="group inline-flex items-center gap-3 px-10 py-5 rounded-full text-lg font-bold text-white"
          style={{ background: "var(--color-primary)", fontFamily: "'Syne', sans-serif" }}>
          Discover <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
        </Link>
        <div className="flex items-center justify-center gap-2 mt-6 text-muted-foreground text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          <MapPin className="w-3.5 h-3.5" /> <span>{DEFAULT_REGION.name}, FL & surrounding areas</span>
        </div>
      </div>
    </section>
  );
}

/* ─── SEO content block: real, useful copy targeting local-intent search.
       Plain prose with internal links — no design disruption. ─── */
function LocalSeoSection() {
  const categories = [
    {
      icon: Trees,
      title: "Outdoors & nature",
      items: ["Robinson Preserve mangrove paddles", "Myakka River State Park", "Celery Fields birding", "Nathan Benderson Park trails"],
    },
    {
      icon: Waves,
      title: "Beaches nearby",
      items: ["Siesta Key (#1 sand in the US)", "Lido Beach & St. Armands Circle", "Anna Maria Island", "Coquina Beach sunsets"],
    },
    {
      icon: Palette,
      title: "Arts & culture",
      items: ["The Ringling museum & mansion", "Marie Selby Botanical Gardens", "Sarasota Opera & Van Wezel", "Music on Main monthly block party"],
    },
    {
      icon: ShoppingBag,
      title: "Shopping & dining",
      items: ["Waterside Place farmers' market", "UTC mall & restaurants", "Main Street at Lakewood Ranch", "St. Armands Circle boutiques"],
    },
    {
      icon: Trophy,
      title: "Sports & family",
      items: ["LECOM Park (Pirates spring training)", "Premier Sports Campus events", "Bradenton Riverwalk & Splash Pad", "Adventure parks & mini golf"],
    },
    {
      icon: CloudRain,
      title: "Rainy day picks",
      items: ["Mote SEA Aquarium", "Big Cat Habitat", "Escape rooms & arcades", "South Florida Museum & planetarium"],
    },
  ];

  return (
    <section className="px-6 md:px-16 lg:px-24 pt-2 pb-12 md:pt-4 md:pb-16 bg-background">
      <div className="max-w-6xl mx-auto">
        <div className="max-w-3xl mb-8">
          <h2
            className="text-2xl md:text-3xl font-black text-foreground mb-3 leading-tight"
            style={{ fontFamily: "'Syne', sans-serif" }}
          >
            What is there to do in Lakewood Ranch, FL?
          </h2>
          <p
            className="text-base md:text-lg text-muted-foreground leading-relaxed"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Tucked between Sarasota and Bradenton on Florida's Gulf Coast, Lakewood Ranch
            puts hundreds of activities within a 30-minute drive — from world-class beaches
            to mangrove paddles, museums, and family-friendly weekends.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((cat) => {
            const Icon = cat.icon;
            return (
              <div
                key={cat.title}
                className="rounded-2xl border border-border bg-card p-5 hover:border-primary/40 transition-colors"
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="rounded-lg bg-primary/10 p-2">
                    <Icon className="h-4 w-4 text-primary" />
                  </div>
                  <h3
                    className="text-base font-black text-foreground"
                    style={{ fontFamily: "'Syne', sans-serif" }}
                  >
                    {cat.title}
                  </h3>
                </div>
                <ul className="space-y-1.5 text-sm text-muted-foreground" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {cat.items.map((item) => (
                    <li key={item} className="flex gap-2">
                      <span className="text-primary mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <p
          className="mt-8 text-base md:text-lg text-muted-foreground leading-relaxed max-w-3xl"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          RanchPlanner pulls all of this together —{" "}
          <Link to="/discover" className="text-primary font-semibold hover:underline">
            tell us your style
          </Link>{" "}
          for a personalized shortlist,{" "}
          <Link to="/explore" className="text-primary font-semibold hover:underline">
            browse every spot
          </Link>
          , or{" "}
          <Link to="/plan" className="text-primary font-semibold hover:underline">
            save your itinerary
          </Link>{" "}
          for the weekend.
        </p>
      </div>
    </section>
  );
}

/* ─── Page ─── */
export function HomePage() {
  return (
    <div className="min-h-screen overflow-x-hidden">
      <HeroSection />
      <HowItWorksStrip />
      <FeaturedAndWeather />
      <LocalSeoSection />
      <FinalCTA />
    </div>
  );
}
